import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Trash2, Sparkles, MapPin, Link2Off, ArrowUp, ArrowDown } from "lucide-react";
import { RichDescriptionEditor } from "@/components/RichDescriptionEditor";
import { API } from "@/App";

const LineItemCard = ({ item, index, areaOptions = [], onLinkArea, onUpdate, onRemove, onMove, canMoveUp = false, canMoveDown = false }) => {
  const [drafting, setDrafting] = useState(false);
  const isMobilization = item.service_name?.toLowerCase().includes("mobilization");
  const isTotalOnly = item.total_only === true;
  const showBreakdown = !isMobilization && !isTotalOnly;
  const isLinked = !!item.area_link && areaOptions.length > 0;
  const linkedLabel = isLinked
    ? (areaOptions.find((o) => o.value === item.area_link)?.label || "Linked area")
    : null;

  const handleDraftDescription = async () => {
    if (!item.service_name?.trim()) {
      toast.error("Enter a service name first");
      return;
    }
    setDrafting(true);
    try {
      const resp = await axios.post(`${API}/ai/draft-description`, {
        service_name: item.service_name,
        current_description: item.description || "",
      });
      onUpdate(item.id, "description", resp.data.description);
      toast.success("Description drafted by AI");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI draft failed");
    } finally {
      setDrafting(false);
    }
  };

  return (
    <div className="border border-zinc-200 rounded-lg p-4 bg-zinc-50/50" data-testid={`line-item-${index}`}>
      <div className="flex items-start justify-between mb-3">
        <Input
          value={item.service_name}
          onChange={(e) => onUpdate(item.id, "service_name", e.target.value)}
          className="font-semibold text-[#00341d] bg-transparent border-none p-0 h-auto text-base focus:ring-0 flex-1"
        />
        <div className="flex items-center gap-0.5">
          {onMove && (
            <>
              <Button variant="ghost" size="sm" onClick={() => onMove(item.id, "up")} disabled={!canMoveUp} className="text-[#00341d] h-8 w-8 p-0 disabled:opacity-30" data-testid={`item-move-up-${item.id}`} title="Move item up">
                <ArrowUp className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onMove(item.id, "down")} disabled={!canMoveDown} className="text-[#00341d] h-8 w-8 p-0 disabled:opacity-30" data-testid={`item-move-down-${item.id}`} title="Move item down">
                <ArrowDown className="w-4 h-4" />
              </Button>
            </>
          )}
          <Button variant="ghost" size="sm" onClick={() => onRemove(item.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Description with AI draft button */}
      <div className="mb-4">
        <RichDescriptionEditor
          value={item.description}
          onChange={(html) => onUpdate(item.id, "description", html)}
          placeholder="Description..."
          testId={`line-item-desc-${index}`}
          rightSlot={
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={handleDraftDescription}
              disabled={drafting}
              data-testid={`draft-desc-btn-${index}`}
              title="Draft description with AI"
              className="h-6 px-2 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50 border border-amber-200 rounded gap-1"
            >
              {drafting
                ? <><span className="w-3 h-3 border border-amber-500 border-t-transparent rounded-full animate-spin inline-block" /> Drafting...</>
                : <><Sparkles className="w-3 h-3" /> Draft AI</>
              }
            </Button>
          }
        />
      </div>
      
      {isMobilization ? (
        <div className="flex items-center justify-between">
          <span className="text-sm text-zinc-500">Total</span>
          <div className="flex items-center gap-2">
            <span className="text-zinc-400">$</span>
            <Input 
              type="number" 
              step="0.01" 
              value={item.total || 0} 
              onChange={(e) => {
                const newTotal = parseFloat(e.target.value) || 0;
                onUpdate(item.id, "total", newTotal);
                onUpdate(item.id, "quantity", newTotal / 4);
                onUpdate(item.id, "unit_price", 4);
              }} 
              className="h-10 w-32 text-right font-bold text-[#00341d]" 
            />
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id={`total-only-${item.id}`}
              checked={item.total_only || false}
              onChange={(e) => onUpdate(item.id, "total_only", e.target.checked)}
              className="rounded border-zinc-300"
            />
            <Label htmlFor={`total-only-${item.id}`} className="text-xs text-zinc-500 cursor-pointer">
              Enter total only (hide qty/unit/price breakdown)
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id={`optional-${item.id}`}
              checked={item.optional || false}
              onChange={(e) => {
                onUpdate(item.id, "optional", e.target.checked);
                // When marking as optional, default to selected
                if (e.target.checked && item.accepted === undefined) {
                  onUpdate(item.id, "accepted", true);
                }
              }}
              className="rounded border-amber-400 text-amber-500"
            />
            <Label htmlFor={`optional-${item.id}`} className="text-xs text-amber-600 cursor-pointer font-medium">
              Optional — customer can accept or decline this item
            </Label>
          </div>
          
          {/* Show selected by default option when item is optional */}
          {item.optional && (
            <div className="flex items-center gap-2 ml-6 mt-1">
              <input
                type="checkbox"
                id={`accepted-${item.id}`}
                checked={item.accepted !== false}
                onChange={(e) => onUpdate(item.id, "accepted", e.target.checked)}
                className="rounded border-zinc-300 text-zinc-500"
              />
              <Label htmlFor={`accepted-${item.id}`} className="text-xs text-zinc-500 cursor-pointer">
                Selected by default when customer views quote
              </Label>
            </div>
          )}

          {/* Customer-adjustable quantity — only for optional, quantity-priced items (not total-only, not area-linked) */}
          {item.optional && showBreakdown && !item.area_link && (
            <div className="flex items-center gap-2 ml-6 mt-1">
              <input
                type="checkbox"
                id={`cust-qty-${item.id}`}
                checked={item.customer_adjustable_qty || false}
                onChange={(e) => onUpdate(item.id, "customer_adjustable_qty", e.target.checked)}
                className="rounded border-amber-400 text-amber-500"
                data-testid={`customer-adjustable-qty-${item.id}`}
              />
              <Label htmlFor={`cust-qty-${item.id}`} className="text-xs text-amber-600 cursor-pointer">
                Let customer choose the quantity (the quantity below is the starting value)
              </Label>
            </div>
          )}
          
          {showBreakdown ? (
            <>
              {areaOptions.length > 0 && (
                <div className="flex items-center gap-2 rounded-md bg-[#00341d]/[0.03] border border-[#00341d]/10 px-3 py-2" data-testid={`area-link-row-${index}`}>
                  <MapPin className="w-4 h-4 text-[#00341d] shrink-0" />
                  <Label className="text-xs text-zinc-600 whitespace-nowrap">Quantity from area</Label>
                  <Select
                    value={item.area_link || "none"}
                    onValueChange={(v) => onLinkArea?.(item.id, v === "none" ? null : v)}
                  >
                    <SelectTrigger className="h-8 flex-1 bg-white" data-testid={`area-link-select-${index}`}>
                      <SelectValue placeholder="Not linked — manual quantity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not linked — manual quantity</SelectItem>
                      {areaOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isLinked && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onLinkArea?.(item.id, null)}
                      className="h-8 px-2 text-xs text-zinc-500 hover:text-red-600"
                      data-testid={`area-unlink-${index}`}
                      title="Unlink — type quantity manually"
                    >
                      <Link2Off className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              )}
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs text-zinc-500">Quantity{isLinked ? " (from area)" : ""}</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={item.quantity}
                    onChange={(e) => onUpdate(item.id, "quantity", parseFloat(e.target.value) || 0)}
                    disabled={isLinked}
                    className={`h-10 mt-1 ${isLinked ? "bg-[#00341d]/5 text-[#00341d] font-semibold" : ""}`}
                    data-testid={`line-item-qty-${index}`}
                  />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">Unit</Label>
                  <Input value={item.unit} onChange={(e) => onUpdate(item.id, "unit", e.target.value)} className="h-10 mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">Unit Price ($)</Label>
                  <Input type="number" step="0.01" value={item.unit_price} onChange={(e) => onUpdate(item.id, "unit_price", parseFloat(e.target.value) || 0)} className="h-10 mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">Total</Label>
                  <div className="h-10 mt-1 flex items-center font-bold text-[#00341d]">
                    ${item.total?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
              {isLinked && (
                <p className="text-[11px] text-[#00341d]/70" data-testid={`area-link-note-${index}`}>
                  Quantity auto-syncs with <span className="font-medium">{linkedLabel}</span>.
                </p>
              )}
            </>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-500">Total</span>
              <div className="flex items-center gap-2">
                <span className="text-zinc-400">$</span>
                <Input 
                  type="number" 
                  step="0.01" 
                  value={item.total || 0} 
                  onChange={(e) => onUpdate(item.id, "total", parseFloat(e.target.value) || 0)} 
                  className="h-10 w-32 text-right font-bold text-[#00341d]" 
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LineItemCard;
